import React from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbIcon from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import {
  AgentMessageEventChatCompressionDataInput,
  AgentMessageEventChatCompressionStatus,
} from '../../../../gamma-api';
import IbSpin from '../../common/IbSpin';

const MAIN_CLASS_NAME = 'chat-compression-event';
const SELECTED_CLASS_NAME = `${MAIN_CLASS_NAME}_selected`;
const ERROR_CLASS_NAME = `${MAIN_CLASS_NAME}_error`;
const SUCCESS_CLASS_NAME = `${MAIN_CLASS_NAME}_success`;
const DEFAULT_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-default`;
const SMALL_SIZE_CLASS_NAME = `${MAIN_CLASS_NAME}_size-small`;
const ICON_CONTAINER_CLASS_NAME = `${MAIN_CLASS_NAME}__icon-container`;
const STATUS_CLASS_NAME = `${MAIN_CLASS_NAME}__status`;

export enum ChatCompressionEventSize {
  DEFAULT = 'default',
  SMALL = 'small',
}

const iconSizeMap = {
  [ChatCompressionEventSize.DEFAULT]: 20,
  [ChatCompressionEventSize.SMALL]: 16,
};

const paragraphTypeMap = {
  [ChatCompressionEventSize.DEFAULT]: 'normal' as const,
  [ChatCompressionEventSize.SMALL]: 'secondary' as const,
};

const spinSizeMap = {
  [ChatCompressionEventSize.DEFAULT]: 'default' as const,
  [ChatCompressionEventSize.SMALL]: 'small' as const,
};

export interface IChatCompressionEventProps {
  chatCompressionData: AgentMessageEventChatCompressionDataInput;
  isSelected?: boolean;
  size?: ChatCompressionEventSize;
  onSelect?: () => void;
}

const ChatCompressionEvent: React.FC<IChatCompressionEventProps> = ({
  chatCompressionData,
  isSelected = false,
  size = ChatCompressionEventSize.DEFAULT,
  onSelect,
}) => {
  const { t } = useTranslation();

  const classes = [MAIN_CLASS_NAME];
  if (isSelected) {
    classes.push(SELECTED_CLASS_NAME);
  }
  if (chatCompressionData.status === AgentMessageEventChatCompressionStatus.Error) {
    classes.push(ERROR_CLASS_NAME);
  } else if (chatCompressionData.status === AgentMessageEventChatCompressionStatus.Success) {
    classes.push(SUCCESS_CLASS_NAME);
  }
  if (size === ChatCompressionEventSize.DEFAULT) {
    classes.push(DEFAULT_SIZE_CLASS_NAME);
  } else if (size === ChatCompressionEventSize.SMALL) {
    classes.push(SMALL_SIZE_CLASS_NAME);
  }

  const onClick = () => onSelect?.();

  return (
    <div className={classes.join(' ')} onClick={onClick}>
      <div className={ICON_CONTAINER_CLASS_NAME}>
        <IbIcon iconName="compression" size={iconSizeMap[size]} />
      </div>
      <IbTypography.Paragraph ellipsis type={paragraphTypeMap[size]}>
        {t('Agent: Chat compression event: Title')}
      </IbTypography.Paragraph>
      {chatCompressionData.status === AgentMessageEventChatCompressionStatus.Success && (
        <IbIcon className={STATUS_CLASS_NAME} iconName="check-one" size={iconSizeMap[size]} />
      )}
      {chatCompressionData.status === AgentMessageEventChatCompressionStatus.Error && (
        <IbIcon className={STATUS_CLASS_NAME} iconName="attention" size={iconSizeMap[size]} />
      )}
      {chatCompressionData.status === AgentMessageEventChatCompressionStatus.Processing && (
        <IbSpin className={STATUS_CLASS_NAME} size={spinSizeMap[size]} />
      )}
    </div>
  );
};

export default ChatCompressionEvent;
